import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Building2, Loader2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useContactSearch } from "@/modules/contacts/hooks/useContacts";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { useAddTeamMember } from "../hooks/useTeam";
import type { TeamEntityType, TeamMember } from "../types";

const CARD_BORDER = "hsl(210,30%,85%)";

type Tab = "internal" | "external";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  entityType: TeamEntityType;
  entityId: string;
  existingMembers: TeamMember[];
};

export function AddTeamMemberDrawer({
  isOpen,
  onClose,
  entityType,
  entityId,
  existingMembers,
}: Props) {
  const [tab, setTab] = useState<Tab>("internal");
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [roleValue, setRoleValue] = useState<string>("");
  const [isLead, setIsLead] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const usersQuery = useUserList({ page: 1, limit: 100, status: "ACTIVE" });
  const contactsQuery = useContactSearch(search, isOpen && tab === "external");
  const rolesQuery = useCategoryItems("team.role");

  const addMember = useAddTeamMember(entityType, entityId);

  const existingUserIds = useMemo(
    () =>
      new Set(
        existingMembers
          .map((m) => m.userId)
          .filter((id): id is string => !!id),
      ),
    [existingMembers],
  );
  const existingContactIds = useMemo(
    () =>
      new Set(
        existingMembers
          .map((m) => m.contactId)
          .filter((id): id is string => !!id),
      ),
    [existingMembers],
  );

  useEffect(() => {
    if (!isOpen) {
      setTab("internal");
      setSearch("");
      setSelectedUserId(null);
      setSelectedContactId(null);
      setRoleValue("");
      setIsLead(false);
      setError(null);
    }
  }, [isOpen]);

  const allUsers = usersQuery.data?.data ?? [];
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return allUsers;
    const term = search.toLowerCase();
    return allUsers.filter(
      (u) =>
        u.name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term) ||
        (u.code ?? "").toLowerCase().includes(term),
    );
  }, [allUsers, search]);

  const contacts = contactsQuery.data?.data ?? [];
  const roles = rolesQuery.data ?? [];

  const canSubmit =
    !submitting &&
    ((tab === "internal" && selectedUserId) ||
      (tab === "external" && selectedContactId));

  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      await addMember.mutateAsync({
        entityType,
        entityId,
        userId: tab === "internal" ? selectedUserId ?? undefined : undefined,
        contactId:
          tab === "external" ? selectedContactId ?? undefined : undefined,
        roleValue: roleValue || null,
        isLead,
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add team member",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[460px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: CARD_BORDER }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: CARD_BORDER }}
        >
          <h2 className="text-lg font-semibold text-foreground">
            Add Team Member
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div
          className="flex items-center gap-1 border-b px-2"
          style={{ borderColor: CARD_BORDER }}
        >
          {(["internal", "external"] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTab(t);
                setSearch("");
                setSelectedUserId(null);
                setSelectedContactId(null);
              }}
              className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                tab === t
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t === "internal" ? "Internal (User)" : "External (Contact)"}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 p-6">
            <div className="space-y-2">
              <Label>{tab === "internal" ? "Search users" : "Search contacts"}</Label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder={
                    tab === "internal"
                      ? "Search by name, email, or code…"
                      : "Search contacts…"
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 pl-9 text-sm"
                />
              </div>
            </div>

            <div
              className="overflow-hidden rounded-lg border"
              style={{ borderColor: CARD_BORDER, maxHeight: 260 }}
            >
              {tab === "internal" ? (
                usersQuery.isLoading ? (
                  <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      strokeWidth={1.5}
                    />
                    Loading…
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No users found.
                  </div>
                ) : (
                  <div className="max-h-[260px] divide-y divide-border overflow-y-auto">
                    {filteredUsers.map((u) => {
                      const added = existingUserIds.has(u.id);
                      const selected = selectedUserId === u.id;
                      return (
                        <label
                          key={u.id}
                          className={`flex cursor-pointer items-center gap-3 px-3 py-2 ${
                            added
                              ? "cursor-not-allowed opacity-60"
                              : selected
                                ? "bg-[hsl(210,55%,92%)]"
                                : "hover:bg-[hsl(210,50%,97%)]"
                          }`}
                        >
                          <input
                            type="radio"
                            name="team-user"
                            disabled={added}
                            checked={selected}
                            onChange={() =>
                              !added && setSelectedUserId(u.id)
                            }
                          />
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-[11px] font-semibold text-[hsl(210,100%,28%)]">
                            {u.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-foreground">
                              {u.name}
                            </div>
                            <div className="truncate text-xs text-muted-foreground">
                              {u.email}
                              {u.code ? ` · ${u.code}` : ""}
                            </div>
                          </div>
                          {added && (
                            <span
                              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                              style={{
                                background: "hsl(142,55%,92%)",
                                color: "hsl(142,72%,28%)",
                              }}
                            >
                              On team
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )
              ) : !search.trim() ? (
                <div className="py-8 text-center text-xs text-muted-foreground">
                  Type to search contacts…
                </div>
              ) : contactsQuery.isLoading ? (
                <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    strokeWidth={1.5}
                  />
                  Loading…
                </div>
              ) : contacts.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No contacts found.
                </div>
              ) : (
                <div className="max-h-[260px] divide-y divide-border overflow-y-auto">
                  {contacts.map((c) => {
                    const added = existingContactIds.has(c.id);
                    const selected = selectedContactId === c.id;
                    return (
                      <label
                        key={c.id}
                        className={`flex cursor-pointer items-center gap-3 px-3 py-2 ${
                          added
                            ? "cursor-not-allowed opacity-60"
                            : selected
                              ? "bg-[hsl(38,92%,92%)]"
                              : "hover:bg-[hsl(210,50%,97%)]"
                        }`}
                      >
                        <input
                          type="radio"
                          name="team-contact"
                          disabled={added}
                          checked={selected}
                          onChange={() =>
                            !added && setSelectedContactId(c.id)
                          }
                        />
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(38,92%,92%)] text-[11px] font-semibold text-[hsl(38,92%,30%)]">
                          {c.fullName
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-foreground">
                            {c.fullName}
                          </div>
                          <div className="flex items-center gap-2 truncate text-xs text-muted-foreground">
                            {c.companyName && (
                              <span className="inline-flex items-center gap-1">
                                <Building2
                                  className="h-3 w-3"
                                  strokeWidth={1.5}
                                />
                                {c.companyName}
                              </span>
                            )}
                            {c.email && <span>· {c.email}</span>}
                          </div>
                        </div>
                        {added && (
                          <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                            style={{
                              background: "hsl(142,55%,92%)",
                              color: "hsl(142,72%,28%)",
                            }}
                          >
                            On team
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="team-role">Role</Label>
              <Select
                id="team-role"
                value={roleValue}
                onChange={(e) => setRoleValue(e.target.value)}
              >
                <option value="">— No role —</option>
                {roles
                  .filter((r) => r.isActive)
                  .map((r) => (
                    <option key={r.id} value={r.value}>
                      {r.label}
                    </option>
                  ))}
              </Select>
            </div>

            <div className="space-y-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isLead}
                  onChange={(e) => setIsLead(e.target.checked)}
                />
                Designate as Lead
              </label>
              <p className="text-[11px] text-muted-foreground">
                Only one team member can be Lead. Setting this clears any
                existing Lead on this entity.
              </p>
            </div>

            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {error}
              </div>
            )}
          </div>
        </div>

        <div
          className="flex items-center justify-end gap-2 border-t px-6 py-3"
          style={{ borderColor: CARD_BORDER }}
        >
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={!canSubmit} onClick={handleSubmit}>
            {submitting && (
              <Loader2
                className="mr-1 h-3.5 w-3.5 animate-spin"
                strokeWidth={1.5}
              />
            )}
            Add Member
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}
